/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { type ProductShelfYearsData } from '../interface';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { type QueryClient } from '@tanstack/react-query';
import type {
  GetShopByYearParamsModel,
  ShopByYearsResponseModel,
} from '@/api/types/product-navigation-search-types';
import {
  getExperimentId,
  getSearchExperimentConfig,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/shop-by-page-year-list`;

type Options = {
  locale: Locale;
  year: string | number | null | undefined;
  preview: boolean;
  makeId: number | null | undefined;
  modelId: number | null | undefined;
  taxonomyPath?: string;
  categoryIdPath?: string;
  partGroupId?: string;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

type OptionsQuery = Omit<Options, 'locale' | 'preview' | 'shouldCallACES' | 'seoUrl'> & {
  seoUrl?: string;
  enabled?: boolean;
};

const getCategoryYears = async (options: Options, axiosInstance?: AxiosInstance) => {
  const {
    locale,
    makeId,
    modelId,
    taxonomyPath,
    categoryIdPath,
    partGroupId,
    year,
    preview,
    experimentId,
    visitorId,
    userId,
  } = options;

  if (!makeId || !modelId || !(taxonomyPath || categoryIdPath || partGroupId)) {
    throw new Error(
      'getCategoryYears requires makeId, modelId, and at least one of: taxonomyPath, categoryIdPath, or partGroupId',
    );
  }

  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  const yearBlockType = year ? 'ALTERNATE' : 'ALL';

  const params: GetShopByYearParamsModel = {
    country,
    customerType,
    salesChannel,
    preview,
    taxonomyPath,
    categoryIdPath,
    partGroupId,
    makeId,
    modelId,
    year: (year as number | undefined) || undefined,
    yearBlockType,
  };

  const normalizedExperimentId = getExperimentId(experimentId, true);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: true,
  });

  const hashedUserId = userId ? await getSHA256(userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
    visitorId,
    userId: hashedUserId,
  });
  const headers = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).get<ShopByYearsResponseModel>(URL, {
    params: enhancedParams,
    ...(headers && { headers }),
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

const selector = (data: ShopByYearsResponseModel): ProductShelfYearsData => {
  const { title, canonicalPath, taxonomyPath, pageType, years } = data;

  return {
    title,
    canonicalPath,
    taxonomyPath,
    pageType,
    shopByMMYList: (years ?? []).map(({ linkDisplayText, makeModelYearPath, seoUrl }) => ({
      makeModelYearPath,
      name: linkDisplayText,
      seoUrl,
    })),
  };
};

const { useData: useCategoryYearsData, prefetch: prefetchCategoryYearsData } = createQuery<
  ProductShelfYearsData,
  Options
>('categoryYears', getCategoryYears);

export const useCategoryYears = ({ enabled = true, ...rest }: OptionsQuery) => {
  const locale = useLocale();
  const experimentId = useExperimentId();
  const { visitorId, userId } = useVertexPersonalizationParams();

  const options: Options = {
    ...rest,
    locale,
    preview: showXMPreviewDate(),
    experimentId,
    visitorId,
    userId,
  };

  return useCategoryYearsData({
    enabled:
      enabled &&
      Boolean(
        rest?.makeId &&
        rest.modelId &&
        (rest.taxonomyPath || rest.categoryIdPath || rest.partGroupId),
      ),
    ...options,
  });
};

export const prefetchCategoryYears = (
  options: Options,
  queryClient: QueryClient,
  axios: AxiosInstance,
) => {
  return prefetchCategoryYearsData(queryClient, options, axios);
};
